/**
 * UK MSISDN handling. The number is the product, so its formatting appears in
 * the checkout panel, in emails and in the top-up lookup, and all three must
 * agree. The top-up flow authenticates on order code PLUS number, so a shape
 * this rejects is a paying customer being told their own number is invalid.
 */
const UK_CC = "44";
const NATIONAL_RE = /^7\d{9}$/;
/**
 * Normalises the shapes a customer actually produces to bare E.164 digits
 * without the plus, or null when it is not a UK mobile number.
 *
 * Handled deliberately: the "+44 (0)7418 …" form printed on most UK business
 * cards, and "44" glued in front of an existing "07…" number, both of which
 * leave a trunk zero after the country code.
 */
export function normalizeUkMsisdn(input) {
    let digits = input.replace(/[^\d+]/g, "");
    if (digits.startsWith("+"))
        digits = digits.slice(1);
    if (digits.startsWith("00"))
        digits = digits.slice(2);
    if (digits.startsWith("0"))
        digits = UK_CC + digits.slice(1);
    // A bare national number, typed without the trunk zero. Unambiguous: a UK
    // national mobile is ten digits starting 7, and the top-up flow authenticates
    // on the number, so refusing it tells a paying customer their own number is
    // invalid.
    if (!digits.startsWith(UK_CC) && NATIONAL_RE.test(digits))
        return UK_CC + digits;
    if (!digits.startsWith(UK_CC))
        return null;
    let national = digits.slice(UK_CC.length);
    if (national.startsWith("0"))
        national = national.slice(1);
    // "0447418123456": a trunk zero typed in front of a number that already
    // carried the country code. Stripping the duplicate is unambiguous because a
    // real UK national number is ten digits, so one twelve digits long starting
    // "44" cannot be anything else.
    if (national.length === 12 && national.startsWith(UK_CC)) {
        national = national.slice(UK_CC.length);
    }
    return NATIONAL_RE.test(national) ? UK_CC + national : null;
}
function national(msisdn) {
    const n = normalizeUkMsisdn(msisdn);
    return n ? n.slice(UK_CC.length) : null;
}
/**
 * Display form: "+44 7418 123456".
 *
 * Four then six, which is how a UK mobile is written internationally and
 * how a customer will compare it against their handset. It grouped 4-3-3
 * until 2026-09-15; that spelling is not wrong so much as unfamiliar, and
 * an unfamiliar grouping of the one thing this shop sells makes a customer
 * look twice at their own number.
 */
export function formatUkMsisdn(msisdn) {
    const n = national(msisdn);
    if (!n)
        return msisdn;
    return `+${UK_CC} ${n.slice(0, 4)} ${n.slice(4)}`;
}
/**
 * Masked form for surfaces that must not expose the whole number. Keeps the
 * same 4-6 grouping AND the same length as formatUkMsisdn, since the two
 * appear side by side and a mask of a different width reads as a different
 * number. The equal length is asserted by a test.
 *
 * Unparseable input masks to a placeholder rather than passing through:
 * `esims.msisdn` is written verbatim by the sync job, so returning the input
 * would print an unrecognised upstream shape in full, which is the one thing
 * this function exists to prevent.
 */
export function maskUkMsisdn(msisdn) {
    const n = national(msisdn);
    if (!n)
        return "••• ••• •••";
    return `+${UK_CC} ${n.slice(0, 4)} •••••${n.slice(9)}`;
}
//# sourceMappingURL=msisdn.js.map